import { useEffect, useMemo, useState, type FC } from 'react';
import { useParams } from 'react-router';
import { Link as RouterLink } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Badge, Popconfirm, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link, Space, Text, Title } from '@g2u/ui-kit';

import { RootStore } from 'store';
import { Content, RecurringAvailabilityModal } from 'components';
import { Condition, TechnicianRecurringAvailabilityEntry } from 'types';
import { useTechnician } from 'helpers/hooks/useTechnician';
import {
  decimalToTime,
  formatDailyTiming,
  formatEffectivePeriod,
  formatRecurringDays,
  NO_END_DATE,
  parseApiDate,
  recurringStatusLabel,
} from 'pages/technician/edit/helpers/availability';
import { useTechnicianRecurringAvailabilities } from './hooks/useTechnicianRecurringAvailabilities';
import styles from './recurring-availability.module.scss';

export const TechnicianRecurringAvailabilityPage: FC = observer(() => {
  const { id } = useParams();
  const { setBreadcrumbs } = RootStore;
  const { technician } = useTechnician(id);
  const isBlacklisted = technician?.blacklist === Condition.Yes;
  const availabilityUrl = `/technician/edit/${id}/availability`;

  const {
    recurringAvailabilities,
    isRecurringAvailabilitiesLoading,
    paginationMetadata,
    updateRecurringAvailability,
    isRecurringAvailabilityUpdating,
    deleteRecurringAvailability,
  } = useTechnicianRecurringAvailabilities(id);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedRecurringAvailability, setEditedRecurringAvailability] =
    useState<TechnicianRecurringAvailabilityEntry | null>(null);

  const initialValues = useMemo(
    () =>
      editedRecurringAvailability
        ? {
            startDate: parseApiDate(editedRecurringAvailability.start_date),
            endDate:
              editedRecurringAvailability.end_date === NO_END_DATE
                ? undefined
                : parseApiDate(editedRecurringAvailability.end_date),
            days: editedRecurringAvailability.days,
            startTime: decimalToTime(editedRecurringAvailability.start_time),
            endTime: decimalToTime(editedRecurringAvailability.end_time),
            setting: editedRecurringAvailability.setting as 'N' | 'C',
          }
        : undefined,
    [editedRecurringAvailability],
  );

  useEffect(() => {
    setBreadcrumbs([
      {
        url: '/technician/search',
        title: 'Find a technician',
      },
      {
        url: availabilityUrl,
        title: `Technician File ${id}`,
      },
      {
        title: 'Recurring Availability',
      },
    ]);

    return () => {
      setBreadcrumbs(null);
    };
  }, [id]);

  const handleEdit = (recurringAvailability: TechnicianRecurringAvailabilityEntry) => {
    setEditedRecurringAvailability(recurringAvailability);
    setIsModalOpen(true);
  };

  const handleSubmit = (values: RecurringAvailabilityModal.Payload) => {
    updateRecurringAvailability(
      { technicianId: Number(id), recurringAvailabilityId: editedRecurringAvailability!.ID, ...values },
      { onSuccess: () => setIsModalOpen(false) },
    );
  };

  const columns: ColumnsType<TechnicianRecurringAvailabilityEntry> = [
    {
      key: 'effectivePeriod',
      title: 'Effective Period',
      width: 186.67,
      render: (_, record) => formatEffectivePeriod(record.start_date, record.end_date),
    },
    {
      key: 'recurringDays',
      title: 'Recurring Days',
      width: 186.67,
      render: (_, record) => formatRecurringDays(record.days),
    },
    {
      key: 'dailyTiming',
      title: 'Daily Timing',
      width: 186.67,
      render: (_, record) => formatDailyTiming(record.start_time, record.end_time),
    },
    {
      key: 'status',
      title: 'Status',
      width: 130,
      render: (_, record) => recurringStatusLabel(record.setting),
    },
    {
      key: 'addedBy',
      title: 'Added By',
      width: 130,
      render: (_, record) =>
        record.added_by ? (
          <>
            <div>{record.added_by.role}</div>
            <div>({record.added_by.name})</div>
          </>
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 100,
      render: (_, record) => (
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
          <Link.$ disabled={record.is_past} onClick={() => !record.is_past && handleEdit(record)}>
            Edit
          </Link.$>
          <Popconfirm
            icon={false}
            disabled={record.is_past}
            title={<Text.$>Delete the Recurring Availability</Text.$>}
            description="Are you sure you want to delete this Recurring Availability?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => deleteRecurringAvailability({ technicianId: Number(id), recurringAvailabilityId: record.ID })}
          >
            <Link.$ type="danger" disabled={record.is_past}>
              Delete
            </Link.$>
          </Popconfirm>
        </Space.$>
      ),
    },
  ];

  return (
    <Content dataTestId="technician-recurring-availability-page">
      <title>Recurring Availability</title>
      <Badge.Ribbon
        text={
          <>
            Technician: {technician?.contact1_title} {technician?.contact1_firstname} {technician?.contact1_surname}{' '}
            {isBlacklisted ? '| Blacklisted' : ''}
          </>
        }
        color={isBlacklisted ? 'red' : 'blue'}
        placement="start"
        style={{ top: 12 }}
      >
        <Space.$
          direction={Space.Direction.Vertical}
          size={Space.Size.Large}
          className={styles.recurringAvailability__container}
        >
          <RouterLink to={availabilityUrl}>
            <ArrowLeftOutlined /> Availability
          </RouterLink>
          <Title.$ level={Title.Level.H5} noMargin>
            Recurring Availability
          </Title.$>
          <Table
            rowKey="ID"
            className={styles.recurringAvailability__table}
            columns={columns}
            dataSource={recurringAvailabilities}
            loading={isRecurringAvailabilitiesLoading}
            pagination={{
              total: paginationMetadata.total,
              current: paginationMetadata.current_page,
              pageSize: paginationMetadata.pageSize,
              onChange: paginationMetadata.onChange,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              showTotal: (total) => `Total ${total} items`,
            }}
          />
        </Space.$>
      </Badge.Ribbon>
      <RecurringAvailabilityModal
        isOpen={isModalOpen}
        initialValues={initialValues}
        isLoading={isRecurringAvailabilityUpdating}
        onSubmit={handleSubmit}
        onClose={() => setIsModalOpen(false)}
      />
    </Content>
  );
});
